// A product image from the private bucket — or a placeholder.
//
// Resolves the stored path to a signed URL (batched and cached in
// productImages.ts). If the image fails to load — most likely a signed URL
// that outlived its hour in a tab left open — it asks for a fresh one once.

import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { forgetSignedUrl, signedUrlFor } from '@/lib/productImages';

const key = (value: string | null | undefined) => ['product-image', value ?? ''] as const;

export function useProductImage(value: string | null | undefined) {
  const query = useQuery({
    queryKey: key(value),
    queryFn: () => signedUrlFor(value),
    enabled: !!value,
    staleTime: 45 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });
  return { src: value ? query.data ?? null : null, isLoading: !!value && query.isLoading };
}

export function ProductImage({
  value,
  alt,
  className,
  fallback,
}: {
  value: string | null | undefined;
  alt: string;
  className?: string;
  fallback: ReactNode;
}) {
  const qc = useQueryClient();
  const { src } = useProductImage(value);
  const [failedOnce, setFailedOnce] = useState(false);
  const [broken, setBroken] = useState(false);

  if (!src || broken) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => {
        if (!failedOnce) {
          setFailedOnce(true);
          forgetSignedUrl(value);
          void qc.invalidateQueries({ queryKey: key(value) });
        } else {
          setBroken(true);
        }
      }}
    />
  );
}
